<template>
  <div
    class="value-xl__input relative flex w-full flex-col gap-1"
    :class="[{ error: !!error }]"
    @click="() => emit('click')"
  >
    <p v-if="label" class="text-body-default w-full text-center">{{ label }}</p>
    <div
      class="old-input-wrapper text-body-large bg-input-bg-default hover:bg-input-bg-hover focus-within:bg-input-bg-active! disabled:bg-input-bg-disabled text-input-fgr-filled hover:text-input-fgr-hover focus-within:text-input-fgr-active! disabled:text-input-fgr-disabled border-input-bdr-default focus-within:border-input-bdr-active! hover:border-input-bdr-hover relative flex h-16 cursor-text items-center justify-between gap-2 overflow-hidden rounded-full border p-4"
      @click.self="focusInput"
    >
      <Button type="button" icon-only :variant="ButtonVariant.Secondary" @click="adjustValue('down')">
        <Icon src="Minus" />
      </Button>
      <div class="flex gap-2">
        <div
          ref="input"
          class="input min-w-5 overflow-hidden whitespace-nowrap"
          contenteditable
          :data-testid="dataTestid"
          @blur="handleBlur"
          @keypress="handleChange"
          @input="syncValue"
          @paste.prevent="handlePaste"
          @keydown.enter.prevent
        >
          {{ inputValue }}
        </div>
        <div class="text-fgr-secondary shrink-0" @click.self="onSuffixClick">
          {{ suffix }}
        </div>
      </div>
      <Button type="button" icon-only :variant="ButtonVariant.Secondary" @click="adjustValue('up')">
        <Icon src="Plus" />
      </Button>
    </div>

    <Message v-if="message" variant="default" class="justify-center">{{ message }}</Message>
    <Message v-if="error" variant="invalid">{{ error }}</Message>
  </div>
</template>

<script setup lang="ts">
  import { ref } from "vue";

  defineOptions({
    name: "ValueXL",
  });

  const _modelValue = defineModel<string | number>();

  const input = ref<HTMLElement | null>(null);

  const props = withDefaults(
    defineProps<{
      error?: string | null;
      label?: string;
      message?: string;
      name?: string;
      suffix?: string;
      type?: "text" | "number";
      dataTestid?: string;
      step?: number;
    }>(),
    {
      error: null,
      label: undefined,
      message: undefined,
      name: "",
      suffix: undefined,
      type: "text",
      dataTestid: undefined,
      step: 1,
    }
  );

  const {
    value: inputValue,
    handleBlur,
    setValue,
  } = useField(() => props.name, undefined, {
    syncVModel: props.name ? false : true,
    controlled: !!props.name,
  });

  const handleChange = ($evt) => {
    const { target, key, ctrlKey, metaKey } = $evt;

    const isDecimalSeparator = key === "." || key === ",";
    if (isDecimalSeparator) {
      if (target.textContent.includes(".") || target.textContent.includes(",")) {
        $evt.preventDefault();
        return;
      }

      if (target.textContent.length === 0) {
        $evt.preventDefault();
        document.execCommand("insertText", false, "0");
      }

      if (key == ",") {
        $evt.preventDefault();
        //insert dot instead of comma
        document.execCommand("insertText", false, ".");
      }
    }

    const isNumeric = !isNaN(Number($evt.key));
    if (!isNumeric && !isDecimalSeparator && !(ctrlKey || metaKey)) {
      $evt.preventDefault();
    }
  };

  // Sync on input, not keypress: paste, iPad predictive text and autocorrect fire no keypress
  const syncValue = ($evt) => {
    const cursorPosition = getCurrentCursorPosition();

    setValue($evt.target.textContent);
    nextTick(() => {
      setCursorPosition(cursorPosition);
    });
  };

  const handlePaste = ($evt: ClipboardEvent) => {
    const text = ($evt.clipboardData?.getData("text") ?? "").replace(",", ".").replace(/[^\d.]/g, "");
    document.execCommand("insertText", false, text);
  };

  const emit = defineEmits(["click", "click:suffix"]);

  const focusInput = ($evt) => {
    //check if click events is right or left from the input
    if ($evt.clientX > input.value?.getBoundingClientRect().right) {
      focusRightSide();
    } else {
      input.value?.focus();
    }
  };

  const focusRightSide = () => {
    const range = document.createRange();
    const sel = window.getSelection();
    range.selectNodeContents(input.value as Node);
    range.collapse(false);
    sel?.removeAllRanges();
    sel?.addRange(range);
  };

  const onSuffixClick = ($evt) => {
    emit("click:suffix");
    focusInput($evt);
  };

  const adjustValue = (dir: "up" | "down") => {
    const currentValue = parseFloat(inputValue.value) || 0;
    if (currentValue === 0 && dir === "down") {
      setValue("0");
      return;
    }
    const newValue = dir === "up" ? currentValue + props.step : currentValue - props.step;

    setValue(newValue.toString());
  };

  const getCurrentCursorPosition = () => {
    const sel = window.getSelection();
    if (sel?.rangeCount) {
      const range = sel.getRangeAt(0);
      return range.startOffset;
    }
    return 0;
  };

  const setCursorPosition = (position: number) => {
    const range = document.createRange();
    const sel = window.getSelection();

    //if input is empty, return
    if (!input.value?.firstChild) return;

    //check if position is bigger than input length and set it to the end
    if (position > input.value?.textContent.length) {
      position = input.value?.textContent.length;
    }

    range.setStart(input.value?.firstChild as Node, position);
    range.collapse(true);
    sel?.removeAllRanges();
    sel?.addRange(range);
  };

  defineExpose({
    input,
  });
</script>
